<template>
	<div
		id="app-main"
		class="main"
		:class="{ 'sidebar-collapsed': sidebarCollapsed, 'sidebar-opened': !sidebarCollapsed }"
	>
		<n-scrollbar
			ref="scrollbar"
			trigger="none"
			:y-placement="isRTL ? 'left' : 'right'"
			content-class="flex min-h-full flex-col"
			:theme-overrides="{
				railInsetVerticalRight: `${toolbarHeight}px 4px 4px auto`,
				railInsetVerticalLeft: `${toolbarHeight}px auto 4px 4px`
			}"
		>
			<ValidationRequestBanner v-if="!isVerified && SHOW_DOCUMENTS_BANNER && isLogged" />

			<Toolbar v-if="isLogged" :boxed="toolbarBoxed" gradient="body" />
			<div
				id="app-view"
				class="view"
				:class="{ boxed, 'view-padded': overridePadded === true, 'view-no-padded': overridePadded === false }"
			>
				<slot />
			</div>
			<MainFooter v-if="footerShown && isLogged" :boxed />
		</n-scrollbar>
	</div>
</template>

<script lang="ts" setup>
import type { RouteLocationNormalizedGeneric } from "vue-router"
import { NScrollbar } from "naive-ui"
import { computed, defineAsyncComponent, onMounted, ref } from "vue"
import { useRoute, useRouter } from "vue-router"
import MainFooter from "@/app-layouts/common/MainFooter.vue"
import Toolbar from "@/app-layouts/common/Toolbar"
import { useAuthStore } from "@/stores/auth"
import { useThemeStore } from "@/stores/theme"

const SHOW_DOCUMENTS_BANNER = false

const ValidationRequestBanner = defineAsyncComponent(() => import("@/app-layouts/common/ValidationRequestBanner.vue"))

const authStore = useAuthStore()
const themeStore = useThemeStore()
const router = useRouter()
const route = useRoute()
const sidebarCollapsed = computed(() => themeStore.sidebar.collapsed)
const footerShown = computed(() => themeStore.isFooterShown)
const themeBoxed = computed<boolean>(() => themeStore.isBoxed)
const overrideBoxed = ref<boolean | undefined>(undefined)
const overridePadded = ref<boolean | undefined>(undefined)
const boxed = computed<boolean>(() => (overrideBoxed.value !== undefined ? overrideBoxed.value : themeBoxed.value))
const isRTL = computed<boolean>(() => themeStore.isRTL)
const toolbarBoxed = computed(() => themeStore.isToolbarBoxed)
const toolbarHeight = computed(() => themeStore.toolbarHeight)
const scrollbar = ref()

const isVerified = computed(() => authStore.isVerified)
const isLogged = computed(() => authStore.isLogged)

function checkThemeOverrides(currentRoute: RouteLocationNormalizedGeneric) {
	if (currentRoute.meta?.theme?.boxed?.enabled !== undefined) {
		overrideBoxed.value = currentRoute.meta.theme.boxed.enabled
	} else {
		overrideBoxed.value = undefined
	}

	if (currentRoute.meta?.theme?.padded?.enabled !== undefined) {
		overridePadded.value = currentRoute.meta.theme.padded.enabled
	} else {
		overridePadded.value = undefined
	}
}

router.beforeEach(checkThemeOverrides)

onMounted(() => {
	checkThemeOverrides(route)

	router.afterEach(() => {
		if (scrollbar?.value?.scrollTo) {
			scrollbar?.value.scrollTo({ top: 0, behavior: "smooth" })
		}
	})
})
</script>

<style lang="scss" scoped>
@import "./variables";

.main {
	width: 100%;
	position: relative;
	padding-left: var(--sidebar-open-width);
	background-color: var(--bg-body-color);
	transition: all var(--sidebar-anim-ease) var(--sidebar-anim-duration);
	display: flex;
	flex-direction: column;

	&.sidebar-collapsed {
		padding-left: var(--sidebar-close-width);
	}

	.view {
		padding: 0 var(--view-padding);
		flex-grow: 1;
		width: 100%;
		margin: 0 auto;
		display: flex;
		flex-direction: column;

		&.boxed {
			max-width: var(--boxed-width);
		}
		&.view-no-padded {
			padding: 0;
		}
	}

	@media (max-width: $sidebar-bp) {
		padding-left: 0px;

		&.sidebar-collapsed {
			padding-left: 0px;
		}

		&.sidebar-opened {
			/*
			transform: scale(0.8) translateX(100%) rotateY(35deg);
			transform-origin: center left;
			border-radius: 16px;
			*/

			overflow: hidden;
			opacity: 0.5;
		}
	}

	@media (min-width: ($sidebar-bp + 1px)) {
		:deep() {
			header.toolbar {
				.logo-box {
					display: none;
				}
			}
		}
	}
}

.direction-rtl {
	.main {
		padding-left: unset;
		padding-right: var(--sidebar-open-width);

		&.sidebar-collapsed {
			padding-left: unset;
			padding-right: var(--sidebar-close-width);
		}

		@media (max-width: $sidebar-bp) {
			padding-right: 0px;

			&.sidebar-collapsed {
				padding-right: 0px;
			}
		}
	}
}
</style>
